import { useCallback } from "react";

/**
 * Canonical implementation of copy text to clipboard.
 * Uses the async Clipboard API if available, otherwise falls back to a textarea.
 * https://developer.mozilla.org/en-US/docs/Web/API/Clipboard_API
 */
export const useCopyToClipboard = () => {
  return useCallback(async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
    } catch {
      // Fall back below.
    }

    // Fallback for environments where the async Clipboard API is unavailable or blocked
    // (e.g. older browsers, non-secure contexts, or permission-restricted webviews).
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    try {
      document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
  }, []);
};
